import { useMutation } from "@tanstack/react-query";
import queryClient from "config/queryClient";
import { CACHE_KEYS, STORAGE_KEYS } from "constants/common";
import toast from "react-hot-toast";
import { userApi } from "services/api";
import { showMutationError } from "utils/common";
import { formatSurveyResponse } from "utils/surveyUtils";
import { generateFileLookUp, uploadMediaFiles } from "utils/uploadUtils";
import { useAuth } from "./useAuth";
import UsersStatusEnum from "constants/UsersStatusEnum";

const useSaveSurveyResponse = ({
  questioner,
  formSchema,
  userType,
  visibleBackButton,
  handleBack = () => {},
}) => {
  const { updateUserAttributes, user } = useAuth();

  const { mutateAsync: saveSurvey } = useMutation({
    mutationFn: userApi.saveUserSurvey,
    onSuccess: () => {
      queryClient.invalidateQueries({
        queryKey: [CACHE_KEYS.QUESTIONER_ANSWER],
      });
    },
  });

  const handleSubmit = async (payload, progress = {}) => {
    const id = toast.loading("Submitting Survey...");
    try {
      const { fileLookUp, files } = generateFileLookUp(payload);

      if (files?.length) {
        const uploadResponse = await uploadMediaFiles({
          fileLookUp,
          files,
          foldername: `survey-files/${userType}/${questioner.title}`,
          toastId: id,
        });

        Object.entries(uploadResponse).forEach(([key, value]) => {
          payload[key] = value;
        });
      }
      toast.loading("Submitting answers...", {
        id,
      });

      //UPLOAD ANY FILE AND GET FILE INFO HERE AND THEN SUBMIT FINAL FORM
      const result = formatSurveyResponse(formSchema, payload);

      await saveSurvey({
        id: questioner.id,
        data: {
          userResponse: result.formattedResponse,
          progress: {
            stepCount: result.stepCount,
            fieldCount: result.fieldCount,
            ...progress,
          },
        },
      });

      toast.success("Response saved successfully");

      if (progress.isFinalStep) {
        localStorage.removeItem(STORAGE_KEYS.ACTIVE_STEP);
        localStorage.removeItem(STORAGE_KEYS.FORM_DATA);
        updateUserAttributes({
          status:
            user?.status === UsersStatusEnum.Approved
              ? UsersStatusEnum.Approved
              : UsersStatusEnum.Pending,
        });
      }

      if (visibleBackButton) {
        handleBack();
      }
    } catch (error) {
      showMutationError(error);
    } finally {
      toast.dismiss(id);
    }
  };

  return {
    handleSubmit,
  };
};

export default useSaveSurveyResponse;
